import { FC, ReactNode, useCallback, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { Button, InlineNotification, Loading } from '@carbon/react';
import { Edit, Launch } from '@carbon/react/icons';
import { Helmet } from 'react-helmet-async';
import { Link, PlainLink } from 'components/link/link';
import { DataTable, type DataTableColumnDef } from 'components/data-table';
import type { TableState } from 'components/data-table/types';
import { PageHeader } from 'components/page-header';
import { DEFAULT_PAGE_SIZE } from 'utils/page-sizes';
import { pageTitle } from 'utils/page-title';
import { formatResource } from 'utils/misc';
import { format8 } from 'utils/number';
import routes from '~/routes';
import { useDivisionOrderAtom, useDivisionOrderLineItemsAtom } from './atoms';
import type { DivisionOrderLineItem } from './types';
import { DivisionOrderSupportingFiles } from './supporting-files';
import { SourceDocumentBanner } from './source-document-banner';
import styles from './page.module.scss';

type LineItemRow = DivisionOrderLineItem & Record<string, unknown>;

const lineItemColumns: DataTableColumnDef<LineItemRow>[] = [
  {
    accessorKey: 'name',
    header: 'Division Order Line Item',
    cell: ({ row }) => {
      const { href, name } = row.original;
      return href ? <Link to={href}>{name}</Link> : name;
    },
  },
  {
    accessorKey: 'lineItemStatus',
    header: 'Line Item Status',
    cell: ({ row }) => row.original.lineItemStatus ?? '--',
  },
  {
    accessorKey: 'interest',
    header: 'Interest',
    meta: { type: 'percent', align: 'right' },
    cell: ({ row }) => format8(row.original.interest),
  },
  {
    accessorKey: 'interestType',
    header: 'Interest Type',
    cell: ({ row }) => row.original.interestType ?? '--',
  },
  {
    id: 'propertyName',
    accessorFn: (li) => li.propertyName ?? li.property?.propertyName,
    header: 'Property Name',
    cell: ({ row }) =>
      row.original.propertyName ?? row.original.property?.propertyName ?? '--',
  },
];

const Detail: FC<{ label: string; value: ReactNode }> = ({ label, value }) => {
  const empty =
    value === null ||
    value === undefined ||
    (typeof value === 'string' && value.length === 0);
  return (
    <div className={styles.detail}>
      <dt className={styles.detailLabel}>{label}</dt>
      <dd className={styles.detailValue}>{empty ? '--' : value}</dd>
    </div>
  );
};

const DivisionOrderShowPage: FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const history = useHistory();
  const doResource = useAtomValue(useDivisionOrderAtom(id));

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const lineItemsResource = useAtomValue(
    useDivisionOrderLineItemsAtom(id, page, pageSize)
  );

  const handleLineItemsLoadData = useCallback(
    ({ page: nextPage, pageSize: nextPageSize }: TableState) => {
      setPage(nextPage);
      setPageSize(nextPageSize);
    },
    []
  );

  const { loading, error, data: divisionOrder } = formatResource(doResource);
  const {
    loading: lineItemsLoading,
    error: lineItemsError,
    data: lineItemsData,
  } = formatResource(lineItemsResource);

  const lineItems = lineItemsData?.results ?? [];
  const lineItemsTotalCount = lineItemsData?.totalCount ?? 0;

  if (loading && !divisionOrder) {
    return (
      <div className={styles.loadingContainer}>
        <Loading description="Loading division order..." withOverlay={false} />
      </div>
    );
  }

  if (error) {
    return (
      <InlineNotification
        kind="error"
        lowContrast
        title="Unable to load division order"
        subtitle={error}
      />
    );
  }

  const name = divisionOrder?.name ?? id;
  // SF deep-link comes from the backend (`salesforceHref` on the show payload).
  // Real SF DOs have it; Provisioned/synthetic rows omit it and we hide the
  // action button so users aren't sent to a broken SF record page.
  const salesforceUrl = divisionOrder?.salesforceHref ?? null;
  // Provisioned/auto-connected DOs (those linked to a source Document) edit
  // through the structuring workflow, which lives on the source document's
  // overview page (mirrors the AFE catalog detail Edit action). SF-sourced rows
  // have no source document and keep the "View in Salesforce" action instead.
  const sourceDocument = divisionOrder?.sourceDocument ?? null;

  return (
    <>
      <Helmet>
        <title>{pageTitle(name)}</title>
      </Helmet>
      <PageHeader
        breadcrumbs={[
          [routes.divisionOrdersPath, 'Division Orders'],
          ['', name],
        ]}
        title={name}
        actions={
          sourceDocument ? (
            <Button
              kind="primary"
              size="md"
              renderIcon={Edit}
              onClick={() =>
                history.push({
                  pathname: `/division_order_documents/${sourceDocument.id}`,
                })
              }
            >
              Edit
            </Button>
          ) : salesforceUrl ? (
            <Button
              kind="tertiary"
              size="md"
              renderIcon={Launch}
              href={salesforceUrl}
              target="_blank"
              rel="noreferrer"
            >
              View in Salesforce
            </Button>
          ) : null
        }
      />

      <div className={styles.pageContent}>
        {/* Provisioned/auto-connected DOs have no SF link — show the
            "Auto-Connected" banner linking to the originating source document. */}
        <SourceDocumentBanner sourceDocument={divisionOrder?.sourceDocument} />

        {/* ─── Division Order Details ─── */}
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>Division Order Details</h3>
          <dl className={styles.detailsGrid}>
            <Detail label="Operator" value={divisionOrder?.operator?.name} />
            <Detail
              label="Insights Entity"
              value={
                divisionOrder?.insightsEntity ? (
                  <Link to={divisionOrder.insightsEntity.href}>
                    {divisionOrder.insightsEntity.name}
                  </Link>
                ) : null
              }
            />
            <Detail
              label="CRM Account"
              value={
                divisionOrder?.account?.name ? (
                  divisionOrder.account.salesforceHref ? (
                    <PlainLink
                      href={divisionOrder.account.salesforceHref}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {divisionOrder.account.name}
                    </PlainLink>
                  ) : (
                    divisionOrder.account.name
                  )
                ) : null
              }
            />
            <Detail label="Owner" value={divisionOrder?.owner} />
            <Detail
              label="Division Order Source"
              value={divisionOrder?.divisionOrderSource}
            />
            <Detail label="Status" value={divisionOrder?.status} />
            <Detail
              label="Date Received from Operator"
              value={divisionOrder?.dateReceivedFromOperator}
            />
            <Detail
              label="Date Returned from Operator"
              value={divisionOrder?.dateReturnedToOperator}
            />
            <Detail
              label="Date Confirmed"
              value={divisionOrder?.dateConfirmed}
            />
            <Detail label="Priority" value={divisionOrder?.priority} />
            <Detail
              label="Shared Drive Path"
              value={divisionOrder?.sharedDrivePath}
            />
          </dl>
        </section>

        {/* ─── Division Order Line Items ─── */}
        <section className={styles.section}>
          <h3 className={styles.sectionTitle}>
            {`Division Order Line Items (${lineItemsTotalCount})`}
          </h3>
          {lineItemsError && (
            <InlineNotification
              kind="error"
              lowContrast
              title="Unable to load line items"
              subtitle={lineItemsError}
            />
          )}
          {!lineItemsError && lineItemsLoading && lineItems.length === 0 && (
            <Loading
              description="Loading line items..."
              withOverlay={false}
              small
            />
          )}
          {!lineItemsError && (lineItems.length > 0 || !lineItemsLoading) && (
            <DataTable<LineItemRow>
              data={lineItems as LineItemRow[]}
              columns={lineItemColumns}
              getRowId={(row) => row.id}
              // Pass the live `page`/`pageSize` state (not the initial
              // constants) so when DataTable remounts after a refetch — its
              // internal `useState(initialTableState)` only runs on mount — it
              // re-initialises with the user's current selection instead of
              // resetting back to DEFAULT_PAGE_SIZE.
              initialTableState={{ page, pageSize }}
              scroll={{
                mode: 'paginated',
                serverDriven: true,
                totalCount: lineItemsTotalCount,
              }}
              onPaginationChange={handleLineItemsLoadData}
              config={{
                globalSearch: false,
                columnFilters: false,
                columnReordering: false,
                columnResizing: false,
                columnPinning: false,
                columnVisibility: false,
                sorting: false,
                export: false,
              }}
            />
          )}
        </section>

        {/* ─── Supporting Files ─── */}
        {divisionOrder?.resources?.supportingFiles?.href && (
          <DivisionOrderSupportingFiles
            href={divisionOrder.resources.supportingFiles.href}
          />
        )}
      </div>
    </>
  );
};

export { DivisionOrderShowPage };
